import { FC, Fragment, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faChartPie,
  faCircleInfo,
  faHome,
  faTableList,
} from '@fortawesome/free-solid-svg-icons';
import { faCandy } from '@fortawesome/sharp-duotone-solid-svg-icons';
import { Hotel, HotelStatistics } from 'ts/hotel/interfaces/HotelResponse';
import { MapHotelContext } from 'contexts/MapHotelContext';
import { MainContext } from 'contexts/MainContext';
import Tabs from 'components/ui/Tabs/Tabs';

interface ResultHotelTabsProps {
  hotels?: Hotel[];
  isMainLoading: boolean;
  poolingFinished?: boolean;
  stat?: HotelStatistics;
}

const ResultHotelTabs: FC<ResultHotelTabsProps> = ({
  hotels,
  isMainLoading,
  poolingFinished,
  stat,
}) => {
  const { t } = useTranslation();
  const { activeTab, activeHotel, setActiveTab } = useContext(MapHotelContext);
  const { userSiteConfig, user } = useContext(MainContext);
  const { showRoomsInTab } = userSiteConfig.hotel;

  const showStat = user?.branchId === 5000 && !!stat?.sysToken;

  const mainTabItems = useMemo(() => {
    const items = [];

    if (userSiteConfig.hotel.displayStyle === '2') {
      items.push({
        value: '6',
        label: (
          <span className="display-flex" dir="ltr">
            <FontAwesomeIcon size="sm" icon={faHome} className="mr-5" color="#900" />
            <span className="display-flex">
              <span className="mr-5 ml-5">{t('hotel.s')}</span>
              <span>{hotels?.filter((hotel) => hotel.visible).length || 0} </span>
              <span className="mr-5 ml-5">{t('of')}</span>
              <span> {hotels?.length || 0}</span>
            </span>
            {!poolingFinished && (
              <span className="ph-10">
                <FontAwesomeIcon icon={faCandy as any} spin className="text-contrast" />
              </span>
            )}
          </span>
        ),
      });
    }

    items.push(
      {
        value: '5',
        disabled: !activeHotel,
        label: (
          <>
            <FontAwesomeIcon size="sm" icon={faCircleInfo} className="mr-5" />
            {t('hotel.info')}
          </>
        ),
      },
      ...(showRoomsInTab
        ? [
            {
              value: '3',
              disabled: !activeHotel,
              label: (
                <>
                  <FontAwesomeIcon size="sm" icon={faBed} className="mr-5" />
                  {t('hotel.rooms')}
                </>
              ),
            },
          ]
        : []),
      {
        value: '1',
        label: (
          <>
            <FontAwesomeIcon size="sm" icon={faTableList} className="mr-5" />
            {t('hotel.room_types')}
          </>
        ),
      },
    );

    if (showStat) {
      items.push({
        value: '4',
        label: (
          <>
            <FontAwesomeIcon size="sm" icon={faChartPie} className="mr-5" />
            Statistic
          </>
        ),
      });
    }

    return items;
  }, [
    activeHotel,
    hotels,
    poolingFinished,
    showRoomsInTab,
    showStat,
    t,
    userSiteConfig.hotel.displayStyle,
  ]);

  const statOnlyItems = useMemo(
    () => [
      {
        value: '4',
        label: (
          <>
            <FontAwesomeIcon size="sm" icon={faChartPie} className="mr-5" />
            Statistic
          </>
        ),
      },
    ],
    [],
  );

  return (
    <Fragment>
      {!isMainLoading && (
        <Tabs
          label={t('hotel.s')}
          className="hotels-tabs hotels-main-tabs"
          value={String(activeTab === 0 ? 6 : activeTab)}
          onValueChange={(v) => setActiveTab(Number(v))}
          items={mainTabItems}
        />
      )}
      {isMainLoading && showStat && (
        <Tabs
          label="Statistic"
          className="hotels-tabs hotels-main-tabs"
          value={String(activeTab)}
          onValueChange={(v) => setActiveTab(Number(v))}
          items={statOnlyItems}
        />
      )}
      {isMainLoading && !showStat && <div style={{ height: 34 }}></div>}
    </Fragment>
  );
};

export default ResultHotelTabs;
